import { useMemo, useState } from 'react'

import { Button, Checkbox, Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import { IColumn, Table } from '@/core/components/Table'
import {
  RewardPoolDto,
  RewardRuleDto,
  RewardRuleUpsertDto,
} from '@/common/models/gamification'
import {
  createRewardRule,
  deleteRewardRule,
  updateRewardRule,
} from '@/core/services/gamification'

import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { RewardRuleEditorModal } from './RewardRuleEditorModal'

interface RewardRuleListProps {
  defId: string
  rules: RewardRuleDto[]
  pools: RewardPoolDto[]
  hasEditPermission: boolean
  /** A display-only reward is never granted, so it cannot have auto-grant rules (API returns 400). */
  displayOnly?: boolean
  onChanged: () => Promise<void> | void
  onError: (e: unknown) => void
}

export const RewardRuleList = ({
  defId,
  rules,
  pools,
  hasEditPermission,
  displayOnly = false,
  onChanged,
  onError,
}: RewardRuleListProps) => {
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<RewardRuleDto | null>(null)
  const [deleting, setDeleting] = useState<RewardRuleDto | null>(null)

  const openCreate = () => {
    setEditing(null)
    setEditorOpen(true)
  }

  const openEdit = (rule: RewardRuleDto) => {
    if (!hasEditPermission) return
    setEditing(rule)
    setEditorOpen(true)
  }

  const handleSubmit = async (data: RewardRuleUpsertDto, id?: string) => {
    try {
      if (id) await updateRewardRule(id, data)
      else await createRewardRule(defId, data)
      await onChanged()
    } catch (e) {
      onError(e)
    }
  }

  const handleDelete = async (rule: RewardRuleDto) => {
    try {
      await deleteRewardRule(rule.id)
      await onChanged()
    } catch (e) {
      onError(e)
    }
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Trigger',
        accessor: 'triggerType',
        render: (row: RewardRuleDto) => (
          <span className="text-monochrome-1">{row.triggerType}</span>
        ),
      },
      {
        title: 'Trigger Ref',
        accessor: 'triggerRef',
        render: (row: RewardRuleDto) => (
          <span className="text-monochrome-2">{row.triggerRef || '—'}</span>
        ),
      },
      {
        title: 'Pool',
        accessor: 'rewardPoolId',
        render: (row: RewardRuleDto) => (
          <span className="text-monochrome-2">{row.rewardPoolId || '—'}</span>
        ),
      },
      {
        title: 'Active',
        accessor: 'active',
        render: (row: RewardRuleDto) => (
          <Checkbox checked={row.active} disabled />
        ),
      },
      {
        title: '',
        accessor: '',
        render: (row: RewardRuleDto) => (
          <div className="flex items-center gap-4">
            <a
              className="text-accent-1 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                openEdit(row)
              }}
            >
              Edit
            </a>
            {hasEditPermission && (
              <a
                className="text-accent-3 underline"
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setDeleting(row)
                }}
              >
                Delete
              </a>
            )}
          </div>
        ),
      },
    ],
    [hasEditPermission],
  )

  return (
    <Card className="p-5 !bg-monochrome-4-60">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-2xl font-bold text-monochrome-1">Rules</div>
        {hasEditPermission && !displayOnly && (
          <Button variant="primary-bordered" onClick={openCreate}>
            Add Rule
            <Icon
              icon="plus-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
            />
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        data={rules}
        noDataMessage={
          displayOnly
            ? 'This reward is display only — it is shown in the app but never granted, so it cannot have rules'
            : 'No rules yet'
        }
      />

      <RewardRuleEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        rule={editing}
        pools={pools}
        onSubmit={handleSubmit}
      />

      <DeleteConfirmationModal
        showDeleteConfirmationModal={!!deleting}
        setDeleteConfirmationModal={(show) => {
          if (!show) setDeleting(null)
        }}
        deletedItem={deleting ? `${deleting.triggerType} rule` : undefined}
        deleteFunc={() => {
          if (deleting) handleDelete(deleting)
          setDeleting(null)
        }}
      />
    </Card>
  )
}
